import { Award, Sparkles, Users, Leaf, Target, Eye, MapPin, Globe } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SITE } from "../lib/site";
import { Link } from "react-router-dom";

const VALUES = [
  { icon: Award, title: "Quality and Excellence", desc: "Every product is developed and tested to ensure the highest standards of purity, safety, efficacy, and consistency." },
  { icon: Users, title: "Innovation and Continuous Improvement", desc: "We continuously invest in research and innovation to create practical healthcare solutions for the evolving needs of Indian dairy and livestock farming." },
  { icon: Sparkles, title: "Integrity and Ethical Business Practices", desc: "We conduct our business with transparency, honesty, and ethical standards while combining modern science with trusted veterinary expertise." },
  { icon: Leaf, title: "Customer-Centric Approach", desc: "Our customers are at the heart of everything we do. We focus on delivering reliable products, responsive support, and long-term value." },
  { icon: Target, title: "Commitment to Animal Welfare", desc: "We are dedicated to improving animal health, well-being, and productivity through safe, effective, and responsible veterinary healthcare solutions." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      {/* Hero */}
      <section className="relative h-[44vh] min-h-[340px] bg-brand-ink overflow-hidden">
        <img src="https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=1800&q=70" alt="Indian dairy farm" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/85 to-brand-ink/40" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full text-white">
            <div className="text-brand-orange font-heading font-bold text-xs tracking-[0.22em] uppercase mb-3">About Us</div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">A Fullfill Desire of Animal Health Care</h1>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-brand-orange font-heading font-bold text-xs tracking-[0.22em] uppercase mb-3">Our Story</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-brand-ink mb-6 tracking-tight">Healthy Animals. Prosperous Farms.</h2>
            <div className="space-y-5 text-base text-brand-ink/80 leading-relaxed">
              <p>Desire Vet Care Pvt. Ltd. is a veterinary healthcare company dedicated to enhancing animal health, productivity, and welfare through quality pharmaceutical products and healthcare solutions. We are committed to supporting veterinarians, dairy farmers, livestock producers, and pet owners with safe, effective, and innovative veterinary medicines and nutritional products.</p>
              <p>At Desire Vet Care, we focus on delivering reliable solutions for the prevention, treatment, and management of animal diseases. Our product portfolio is designed to meet the healthcare needs of livestock, poultry, and companion animals, helping improve their performance, well-being, and quality of life.</p>
              <p>Driven by a commitment to quality, integrity, and customer satisfaction, we continuously strive to provide products that meet industry standards and contribute to sustainable animal husbandry practices. Our experienced team works closely with animal health professionals and farmers to understand their challenges and offer practical healthcare solutions.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-brand-surface py-20">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal>
            <div className="bg-white border border-brand-line rounded-lg p-9 h-full">
              <div className="w-12 h-12 rounded-md bg-brand-orange/10 text-brand-orange flex items-center justify-center mb-5"><Target size={22} /></div>
              <h3 className="font-heading font-extrabold text-2xl text-brand-ink mb-3 tracking-tight">Our Mission</h3>
              <p className="text-base text-brand-ink/75 leading-relaxed">To provide effective veterinary medicines and healthcare solutions that support veterinarians, farmers, and pet owners in achieving better animal health outcomes.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="bg-white border border-brand-line rounded-lg p-9 h-full">
              <div className="w-12 h-12 rounded-md bg-brand-green/10 text-brand-green flex items-center justify-center mb-5"><Eye size={22} /></div>
              <h3 className="font-heading font-extrabold text-2xl text-brand-ink mb-3 tracking-tight">Our Vision</h3>
              <p className="text-base text-brand-ink/75 leading-relaxed">To become a trusted leader in veterinary healthcare by delivering innovative and high-quality products that improve animal health and productivity.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="max-w-2xl mb-12">
              <div className="text-brand-orange font-heading font-bold text-xs tracking-[0.22em] uppercase mb-3">Our Values</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-brand-ink tracking-tight">What We Stand For</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={i} delay={i * 70}>
                <div className="product-card bg-white border border-brand-line rounded-lg p-7 h-full">
                  <div className="w-11 h-11 rounded-md bg-brand-orange/10 text-brand-orange flex items-center justify-center mb-4"><v.icon size={20} /></div>
                  <h4 className="font-heading font-bold text-base text-brand-ink mb-2">{v.title}</h4>
                  <p className="text-sm text-brand-ink/65 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
